import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_COLORS = {
  open: '#f59e0b',
  inProgress: '#38bdf8',
  resolved: '#10b981',
  closed: '#94a3b8',
};
const STATUS_LABELS = { open: 'Open', inProgress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const STATUS_KEYS = ['open', 'inProgress', 'resolved', 'closed'];

function toInputDate(d) {
  return d.toISOString().slice(0, 10);
}

function defaultDates() {
  const today = new Date();
  const past30 = new Date(today);
  past30.setDate(past30.getDate() - 30);
  return { from: toInputDate(past30), to: toInputDate(today) };
}

function niceMax(val) {
  if (!val || val <= 0) return 5;
  const mag = Math.pow(10, Math.floor(Math.log10(val)));
  return Math.ceil(val / mag) * mag;
}

// ─── SVG Stacked Bar Chart ────────────────────────────────────────────────────
function StackedBarChart({ data, labelKey, shortLabelKey }) {
  const [hovIdx, setHovIdx] = useState(null);

  if (!data?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-slate-400">
        <svg className="h-10 w-10 mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-sm">No data for the selected filters</span>
      </div>
    );
  }

  const BAR_W = 54;
  const GAP = 22;
  const PAD_L = 46;
  const PAD_R = 20;
  const PAD_T = 32;
  const PAD_B = 82;
  const SVG_H = 320;

  const maxVal = Math.max(...data.map((d) => d.total), 1);
  const yMax = niceMax(maxVal);
  const plotH = SVG_H - PAD_T - PAD_B;
  const svgW = Math.max(PAD_L + data.length * (BAR_W + GAP) - GAP + PAD_R, 340);

  const toY = (v) => PAD_T + plotH - (v / yMax) * plotH;
  const toH = (v) => (v / yMax) * plotH;

  const STEPS = 5;
  const stepVal = yMax / STEPS;
  const yTicks = Array.from({ length: STEPS + 1 }, (_, i) => ({
    val: Math.round(stepVal * i),
    y: toY(stepVal * i),
  }));

  return (
    <div className="overflow-x-auto w-full">
      <svg width={svgW} height={SVG_H} style={{ display: 'block', minWidth: svgW }}>
        <defs>
          {data.map((d, i) => {
            const x = PAD_L + i * (BAR_W + GAP);
            return (
              <clipPath key={i} id={`bclip-${i}`}>
                <rect x={x} y={toY(d.total) - 1} width={BAR_W} height={toH(d.total) + 2} rx={5} />
              </clipPath>
            );
          })}
        </defs>

        {/* Grid lines + Y labels */}
        {yTicks.map(({ val, y }) => (
          <g key={val}>
            <line x1={PAD_L} y1={y} x2={svgW - PAD_R} y2={y}
              stroke={val === 0 ? '#cbd5e1' : '#f1f5f9'}
              strokeWidth={val === 0 ? 1.5 : 1} />
            <text x={PAD_L - 8} y={y + 4} textAnchor="end"
              fontSize={10} fontFamily="system-ui, sans-serif" fill="#94a3b8">
              {val}
            </text>
          </g>
        ))}

        {/* Bars */}
        {data.map((d, i) => {
          const x = PAD_L + i * (BAR_W + GAP);
          const cx = x + BAR_W / 2;
          const isHov = hovIdx === i;

          let stackY = PAD_T + plotH;
          const segments = STATUS_KEYS.map((key) => {
            const v = d[key] || 0;
            const h = toH(v);
            stackY -= h;
            return { key, v, y: stackY, h };
          });

          return (
            <g key={i} onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}
              style={{ cursor: 'pointer' }}>
              {/* Hover backdrop */}
              {isHov && (
                <rect x={x - 7} y={PAD_T - 4} width={BAR_W + 14}
                  height={plotH + 8} rx={9}
                  fill="#f8fafc" stroke="#e2e8f0" strokeWidth={1} />
              )}

              {/* Stacked segments with rounded-top clip */}
              {d.total > 0 && (
                <g clipPath={`url(#bclip-${i})`}>
                  {segments.map(({ key, v, y, h }) =>
                    v > 0 && (
                      <rect key={key} x={x} y={y} width={BAR_W} height={h}
                        fill={STATUS_COLORS[key]} opacity={isHov ? 1 : 0.84} />
                    )
                  )}
                </g>
              )}

              {/* Empty bar placeholder */}
              {d.total === 0 && (
                <rect x={x} y={PAD_T + plotH - 3} width={BAR_W} height={3}
                  fill="#e2e8f0" rx={2} />
              )}

              {/* Count above bar */}
              {d.total > 0 && (
                <text x={cx} y={toY(d.total) - 7} textAnchor="middle"
                  fontSize={11} fontWeight="700" fontFamily="system-ui, sans-serif"
                  fill={isHov ? '#1e293b' : '#475569'}>
                  {d.total}
                </text>
              )}

              {/* Rotated X-axis label */}
              <text x={cx} y={PAD_T + plotH + 10} textAnchor="end"
                transform={`rotate(-40 ${cx} ${PAD_T + plotH + 10})`}
                fontSize={10} fontFamily="system-ui, sans-serif"
                fill={isHov ? '#4f46e5' : '#64748b'}
                fontWeight={isHov ? '600' : '400'}>
                {(shortLabelKey && d[shortLabelKey]) || d[labelKey]}
              </text>
            </g>
          );
        })}

        {/* Tooltip */}
        {hovIdx !== null && (() => {
          const d = data[hovIdx];
          const x = PAD_L + hovIdx * (BAR_W + GAP);
          const cx = x + BAR_W / 2;
          const TW = 156; const TH = 114;
          const tx = Math.min(Math.max(cx - TW / 2, 4), svgW - TW - 4);
          const ty = Math.max(PAD_T + 4, toY(Math.max(d.total, 1)) - TH - 10);

          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={tx} y={ty} width={TW} height={TH} rx={10}
                fill="white" stroke="#e2e8f0" strokeWidth={1}
                style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.10))' }} />
              <text x={tx + 12} y={ty + 17} fontSize={11} fontWeight="700"
                fontFamily="system-ui, sans-serif" fill="#1e293b">
                {d[labelKey]}
              </text>
              {STATUS_KEYS.map((key, ki) => (
                <g key={key}>
                  <rect x={tx + 12} y={ty + 27 + ki * 18} width={9} height={9} rx={2}
                    fill={STATUS_COLORS[key]} />
                  <text x={tx + 26} y={ty + 36 + ki * 18}
                    fontSize={10} fontFamily="system-ui, sans-serif" fill="#475569">
                    {STATUS_LABELS[key]}:
                    <tspan fontWeight="700" fill="#1e293b"> {d[key] || 0}</tspan>
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────
function Legend() {
  return (
    <div className="flex flex-wrap gap-4 mb-4">
      {STATUS_KEYS.map((key) => (
        <div key={key} className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: STATUS_COLORS[key] }} />
          <span className="text-xs text-slate-600">{STATUS_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
const STAT_STYLES = {
  total:      { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', num: 'text-indigo-900' },
  open:       { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  num: 'text-amber-900' },
  inProgress: { bg: 'bg-sky-50',    border: 'border-sky-200',    text: 'text-sky-700',    num: 'text-sky-900' },
  resolved:   { bg: 'bg-emerald-50',border: 'border-emerald-200',text: 'text-emerald-700',num: 'text-emerald-900' },
  closed:     { bg: 'bg-slate-100', border: 'border-slate-200',  text: 'text-slate-600',  num: 'text-slate-800' },
};

function StatCard({ label, value, styleKey }) {
  const s = STAT_STYLES[styleKey] || STAT_STYLES.total;
  return (
    <div className={`rounded-xl border ${s.bg} ${s.border} px-4 py-3 text-center`}>
      <p className={`text-2xl font-bold ${s.num}`}>{value ?? 0}</p>
      <p className={`text-xs font-semibold mt-0.5 ${s.text}`}>{label}</p>
    </div>
  );
}

// ─── Multi-select issue type dropdown ─────────────────────────────────────────
function TypeDropdown({ types, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selectedIds.size === 0;
  const label = allSelected
    ? 'All Issue Types'
    : selectedIds.size === 1
      ? types.find((t) => selectedIds.has(t.id))?.name || '1 type'
      : `${selectedIds.size} types selected`;

  const toggleAll = () => onChange(new Set());
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-semibold text-slate-600 mb-1">Issue Types</label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 min-w-[180px] justify-between"
      >
        <span className="truncate">{label}</span>
        <svg className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-60 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 max-h-64 overflow-y-auto">
          <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" readOnly checked={allSelected}
              onClick={toggleAll}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600" />
            <span className="text-sm font-semibold text-slate-700">All Types</span>
          </label>
          <hr className="my-1 border-slate-100" />
          {types.map((t) => (
            <label key={t.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" readOnly checked={selectedIds.has(t.id)}
                onClick={() => toggleOne(t.id)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600" />
              <span className="text-sm text-slate-700">{t.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function StudentIssueAnalysis() {
  const { from: df, to: dt } = defaultDates();

  const [issueTypes, setIssueTypes] = useState([]);
  const [fromDate, setFromDate] = useState(df);
  const [toDate, setToDate] = useState(dt);
  const [selectedTypeIds, setSelectedTypeIds] = useState(new Set());

  // "Applied" filters trigger the fetch
  const [appliedFilters, setAppliedFilters] = useState({ from: df, to: dt, typeIds: new Set() });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load issue types for multi-select
  useEffect(() => {
    axiosInstance.get('/contact-admin/issue-types').then((r) => setIssueTypes(r?.data || [])).catch(() => {});
  }, []);

  // Fetch analytics whenever appliedFilters changes
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { from: appliedFilters.from, to: appliedFilters.to };
      if (appliedFilters.typeIds.size > 0) {
        params.typeIds = [...appliedFilters.typeIds].join(',');
      }
      const resp = await axiosInstance.get('/contact-admin/issues/analytics', { params });
      setAnalytics(resp?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleApply = () => {
    setAppliedFilters({ from: fromDate, to: toDate, typeIds: new Set(selectedTypeIds) });
  };

  const dateRangeLabel = (() => {
    const fmt = (s) => {
      if (!s) return '';
      const d = new Date(s);
      return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
    };
    return `${fmt(appliedFilters.from)} – ${fmt(appliedFilters.to)}`;
  })();

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* ── Page header ── */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 leading-tight">Student Issues Analytics</h1>
              {analytics && <p className="text-xs text-slate-500">{dateRangeLabel}</p>}
            </div>
          </div>
          <button
            onClick={() => window.close()}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Issue Raised From</label>
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Issue Raised To</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <TypeDropdown
              types={issueTypes}
              selectedIds={selectedTypeIds}
              onChange={setSelectedTypeIds}
            />
            <button
              onClick={handleApply}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60 transition shadow-sm"
            >
              {loading ? (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
              )}
              {loading ? 'Loading…' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Summary stats */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard label="Total Issues" value={analytics.summary.total} styleKey="total" />
            <StatCard label="Open" value={analytics.summary.open} styleKey="open" />
            <StatCard label="In Progress" value={analytics.summary.inProgress} styleKey="inProgress" />
            <StatCard label="Resolved" value={analytics.summary.resolved} styleKey="resolved" />
            <StatCard label="Closed" value={analytics.summary.closed} styleKey="closed" />
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-slate-200 animate-pulse" />
            ))}
          </div>
        )}

        {/* Chart 1: By Issue Type */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-base font-semibold text-slate-900">Issues by Type</h2>
            <p className="text-xs text-slate-500 mt-0.5">Number of issues raised per issue type, broken down by status</p>
          </div>
          <div className="px-6 py-5">
            {loading ? (
              <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
            ) : (
              <>
                <Legend />
                <StackedBarChart
                  data={analytics?.byType || []}
                  labelKey="typeName"
                />
              </>
            )}
          </div>
        </div>

        {/* Chart 2: By Course */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-base font-semibold text-slate-900">Issues by Course</h2>
            <p className="text-xs text-slate-500 mt-0.5">Number of issues raised per enrolled course, broken down by status</p>
          </div>
          <div className="px-6 py-5">
            {loading ? (
              <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
            ) : (
              <>
                <Legend />
                <StackedBarChart
                  data={analytics?.byCourse || []}
                  labelKey="courseName"
                  shortLabelKey="courseShortName"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
